import React, { useState, useMemo } from 'react';
import { Organization, UserProfile } from '../types';
import { Avatar } from './common';

interface SuperAdminReassignModalProps {
  isOpen: boolean;
  onClose: () => void;
  org: Organization;
  users: UserProfile[];
  onReassignAdmin: (params: {
    orgId: string;
    newAdminEmail: string;
    newAdminId?: string;
    newAdminName?: string;
    reasonCategory: string;
    referenceTicket: string;
    outgoingAdminRole: 'vice_principal' | 'teacher' | 'stepped_down';
  }) => Promise<void>;
}

export const SuperAdminReassignModal: React.FC<SuperAdminReassignModalProps> = ({
  isOpen,
  onClose,
  org,
  users,
  onReassignAdmin
}) => {
  const [targetMode, setTargetMode] = useState<'org_staff' | 'platform_search' | 'email'>('org_staff');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [platformSearchQuery, setPlatformSearchQuery] = useState<string>('');
  const [customEmail, setCustomEmail] = useState<string>('');
  const [customName, setCustomName] = useState<string>('');
  
  const [reasonCategory, setReasonCategory] = useState<string>('board_resolution');
  const [referenceTicket, setReferenceTicket] = useState<string>('');
  const [outgoingAdminRole, setOutgoingAdminRole] = useState<'vice_principal' | 'teacher' | 'stepped_down'>('vice_principal');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Current Admin Profile
  const currentAdminUser = useMemo(() => {
    return users.find(u => u.id === org.adminId);
  }, [users, org.adminId]);

  // Existing School Staff
  const orgStaff = useMemo(() => {
    return users.filter(u => 
      u.id !== org.adminId &&
      (u.orgId === org.id || (org.vicePrincipalEmails || []).includes(u.email || ''))
    ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [users, org]);

  // Platform search results
  const searchResults = useMemo(() => {
    if (!platformSearchQuery.trim() || platformSearchQuery.trim().length < 2) return [];
    const q = platformSearchQuery.toLowerCase().trim();
    return users.filter(u => 
      u.id !== org.adminId &&
      (u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || u.mobileNumber?.includes(q))
    ).slice(0, 10);
  }, [users, org.adminId, platformSearchQuery]);

  if (!isOpen) return null;

  const selectedTargetUser = selectedUserId ? users.find(u => u.id === selectedUserId) : null;

  const resolvedTargetEmail = targetMode === 'email'
    ? customEmail.trim().toLowerCase()
    : (selectedTargetUser?.email || '').trim().toLowerCase();

  const resolvedTargetName = targetMode === 'email'
    ? (customName.trim() || 'Incoming Principal')
    : (selectedTargetUser?.name || 'Incoming Principal');

  const resolvedTargetId = targetMode === 'email' ? undefined : selectedTargetUser?.id;

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!resolvedTargetEmail || !validateEmail(resolvedTargetEmail)) {
      setError('Please provide a valid Google account email for the replacement administrator.');
      return;
    }

    if (currentAdminUser && resolvedTargetEmail === (currentAdminUser.email || '').toLowerCase().trim()) {
      setError('The selected email belongs to the current primary administrator.');
      return;
    }

    if (!referenceTicket.trim()) {
      setError('Institutional verification notes / ticket reference is mandatory for Super Admin recovery.');
      return;
    }

    setIsProcessing(true);
    try {
      await onReassignAdmin({
        orgId: org.id,
        newAdminEmail: resolvedTargetEmail,
        newAdminId: resolvedTargetId,
        newAdminName: resolvedTargetName,
        reasonCategory,
        referenceTicket: referenceTicket.trim(),
        outgoingAdminRole
      });
      onClose();
    } catch (err: any) {
      console.error("Super Admin reassign failed:", err);
      setError(err?.message || 'Failed to reassign administrator.');
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/65 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
      <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-2xl w-full shadow-2xl border border-slate-100 max-h-[92vh] overflow-y-auto">
        
        {/* Header */}
        <div className="flex items-start justify-between pb-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-indigo-50 border border-indigo-100 text-indigo-700 flex items-center justify-center flex-shrink-0">
              <span className="material-icons-round text-2xl">manage_accounts</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-slate-900 leading-tight">Reassign Institution Administrator</h2>
                <span className="px-2 py-0.5 bg-indigo-100 text-indigo-800 text-[10px] font-black uppercase tracking-wide rounded-md">
                  Super Admin
                </span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">Emergency Recovery &amp; Board Governance Authority</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors disabled:opacity-50"
          >
            <span className="material-icons-round text-lg">close</span>
          </button>
        </div>

        {/* Institution Context Card */}
        <div className="mt-4 p-4 bg-slate-50 rounded-2xl border border-slate-200 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
          <div>
            <span className="text-slate-400 font-bold uppercase text-[10px] block">Target Institution</span>
            <span className="font-bold text-slate-900 text-sm">{org.name}</span>
            <p className="text-slate-500 text-[11px] mt-0.5">
              {[org.address?.city, org.address?.state].filter(Boolean).join(', ') || 'Location unlisted'}
            </p>
          </div>
          <div>
            <span className="text-slate-400 font-bold uppercase text-[10px] block">Current Primary Principal</span>
            <span className="font-bold text-slate-900 text-sm">
              {currentAdminUser?.name || 'Administrator'}
            </span>
            <p className="text-slate-500 font-mono text-[11px] mt-0.5">
              {currentAdminUser?.email || 'No email recorded'}
            </p>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3.5 bg-red-50 border border-red-200 rounded-xl text-xs text-red-700 font-medium flex items-center gap-2">
            <span className="material-icons-round text-sm flex-shrink-0">error</span>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-5 space-y-5">
          
          {/* Successor Selection */}
          <div className="space-y-3">
            <label className="block text-xs font-bold text-slate-800 uppercase tracking-wide">
              1. Select Replacement Administrator
            </label>

            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => { setTargetMode('org_staff'); setSelectedUserId(''); setError(null); }}
                className={`py-2 px-2.5 rounded-xl border text-xs font-bold flex items-center justify-center gap-1 transition-all ${
                  targetMode === 'org_staff'
                    ? 'bg-indigo-50 border-indigo-300 text-indigo-700 shadow-2xs'
                    : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                }`}
              >
                <span className="material-icons-round text-sm">group</span>
                <span>School Staff ({orgStaff.length})</span>
              </button>

              <button
                type="button"
                onClick={() => { setTargetMode('platform_search'); setSelectedUserId(''); setError(null); }}
                className={`py-2 px-2.5 rounded-xl border text-xs font-bold flex items-center justify-center gap-1 transition-all ${
                  targetMode === 'platform_search'
                    ? 'bg-indigo-50 border-indigo-300 text-indigo-700 shadow-2xs'
                    : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                }`}
              >
                <span className="material-icons-round text-sm">person_search</span>
                <span>Search Platform</span>
              </button>

              <button
                type="button"
                onClick={() => { setTargetMode('email'); setSelectedUserId(''); setError(null); }}
                className={`py-2 px-2.5 rounded-xl border text-xs font-bold flex items-center justify-center gap-1 transition-all ${
                  targetMode === 'email'
                    ? 'bg-indigo-50 border-indigo-300 text-indigo-700 shadow-2xs'
                    : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
                }`}
              >
                <span className="material-icons-round text-sm">mail</span>
                <span>New Email</span>
              </button>
            </div>

            {targetMode === 'org_staff' && (
              <div>
                {orgStaff.length === 0 ? (
                  <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-center text-slate-500 text-xs">
                    <p className="font-medium">No other teachers or vice principals are registered under this school.</p>
                    <p className="text-[11px] text-slate-400 mt-1">Please use 'Search Platform' or 'New Email' above.</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-44 overflow-y-auto pr-1">
                    {orgStaff.map(staff => {
                      const isSelected = selectedUserId === staff.id;
                      const isVp = staff.isVicePrincipal || (org.vicePrincipalEmails || []).includes(staff.email || '');
                      return (
                        <div
                          key={staff.id}
                          onClick={() => setSelectedUserId(staff.id)}
                          className={`p-2.5 rounded-xl border cursor-pointer flex items-center justify-between transition-all ${
                            isSelected
                              ? 'bg-indigo-50 border-indigo-400 ring-2 ring-indigo-200 shadow-xs'
                              : 'bg-white border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className="flex items-center gap-2.5">
                            <Avatar user={staff} className="w-8 h-8" />
                            <div>
                              <div className="flex items-center gap-1.5">
                                <span className="font-bold text-slate-800 text-xs">{staff.name}</span>
                                {isVp && (
                                  <span className="px-1.5 py-0.2 bg-teal-100 text-teal-800 text-[9px] font-bold rounded">
                                    Vice Principal
                                  </span>
                                )}
                              </div>
                              <span className="text-[11px] text-slate-500 font-mono">{staff.email}</span>
                            </div>
                          </div>
                          <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${isSelected ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300'}`}>
                            {isSelected && <span className="material-icons-round text-[10px]">check</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {targetMode === 'platform_search' && (
              <div className="space-y-2">
                <div className="relative">
                  <span className="material-icons-round absolute left-3 top-2.5 text-slate-400 text-sm">search</span>
                  <input
                    type="text"
                    value={platformSearchQuery}
                    onChange={(e) => setPlatformSearchQuery(e.target.value)}
                    placeholder="Search by name, email, or phone number..."
                    className="w-full pl-9 pr-3 py-2 bg-white border border-slate-300 rounded-xl text-xs focus:ring-2 focus:ring-indigo-500 outline-hidden"
                  />
                </div>
                {platformSearchQuery.trim().length >= 2 && (
                  <div className="space-y-1.5 max-h-40 overflow-y-auto pr-1">
                    {searchResults.length === 0 ? (
                      <p className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-center text-xs text-slate-500">
                        No registered users match "{platformSearchQuery}".
                      </p>
                    ) : (
                      searchResults.map(user => {
                        const isSelected = selectedUserId === user.id;
                        return (
                          <div
                            key={user.id}
                            onClick={() => setSelectedUserId(user.id)}
                            className={`p-2.5 rounded-xl border cursor-pointer flex items-center justify-between transition-all ${
                              isSelected
                                ? 'bg-indigo-50 border-indigo-400 ring-2 ring-indigo-200'
                                : 'bg-white border-slate-200 hover:border-slate-300'
                            }`}
                          >
                            <div className="flex items-center gap-2.5">
                              <Avatar user={user} className="w-8 h-8" />
                              <div>
                                <span className="font-bold text-slate-800 text-xs block">{user.name}</span>
                                <span className="text-[11px] text-slate-500 font-mono">{user.email} • Role: {user.role}</span>
                              </div>
                            </div>
                            <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${isSelected ? 'border-indigo-600 bg-indigo-600 text-white' : 'border-slate-300'}`}>
                              {isSelected && <span className="material-icons-round text-[10px]">check</span>}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            )}

            {targetMode === 'email' && (
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 space-y-3 text-xs">
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">
                    Successor Google / Gmail Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={customEmail}
                    onChange={(e) => setCustomEmail(e.target.value)}
                    placeholder="successor.principal@gmail.com"
                    required={targetMode === 'email'}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs focus:ring-2 focus:ring-indigo-500 outline-hidden"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    When this user signs in, they will automatically gain primary administrative ownership.
                  </p>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-slate-700 mb-1">
                    Successor Full Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    placeholder="e.g. Dr. Rajesh Sharma"
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs focus:ring-2 focus:ring-indigo-500 outline-hidden"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Institutional Reason & Verification Reference (Mandatory) */}
          <div className="space-y-3 pt-2 border-t border-slate-100">
            <label className="block text-xs font-bold text-slate-800 uppercase tracking-wide">
              2. Institutional Authorization &amp; Justification <span className="text-red-500">*</span>
            </label>

            <div>
              <label className="block text-[11px] font-bold text-slate-600 mb-1">Authorization Category</label>
              <select
                value={reasonCategory}
                onChange={(e) => setReasonCategory(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-medium text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-hidden"
              >
                <option value="board_resolution">Official School Board / Trust Resolution</option>
                <option value="letterhead_request">Signed Letterhead Request from Management / Chairman</option>
                <option value="emergency_departure">Emergency Departure / Inaccessible Email Account</option>
                <option value="annual_rotation">Institutional Principal Rotation / Transfer</option>
                <option value="account_recovery">Recovery of Orphaned Institution Profile</option>
                <option value="other">Other Verified Institutional Authorization</option>
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-bold text-slate-600 mb-1">
                Verification Notes &amp; Ticket / Document Reference <span className="text-red-500">*</span>
              </label>
              <textarea
                value={referenceTicket}
                onChange={(e) => setReferenceTicket(e.target.value)}
                placeholder="e.g. Board Resolution #2026-BR-812 verified via official school domain email and phone confirmation with Chairman."
                required
                rows={2}
                className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs text-slate-800 focus:ring-2 focus:ring-indigo-500 outline-hidden"
              />
            </div>
          </div>

          {/* Outgoing Admin Status */}
          <div className="space-y-2 pt-2 border-t border-slate-100">
            <label className="block text-xs font-bold text-slate-800 uppercase tracking-wide">
              3. Outgoing Administrator Resolution
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <label
                className={`p-2.5 rounded-xl border flex flex-col justify-between cursor-pointer transition-all ${
                  outgoingAdminRole === 'vice_principal'
                    ? 'bg-teal-50 border-teal-300 ring-2 ring-teal-200'
                    : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="saOutgoingRole"
                    value="vice_principal"
                    checked={outgoingAdminRole === 'vice_principal'}
                    onChange={() => setOutgoingAdminRole('vice_principal')}
                    className="text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-xs font-bold text-slate-900">Demote to VP</span>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Keeps Co-Admin rights.</p>
              </label>

              <label
                className={`p-2.5 rounded-xl border flex flex-col justify-between cursor-pointer transition-all ${
                  outgoingAdminRole === 'teacher'
                    ? 'bg-indigo-50 border-indigo-300 ring-2 ring-indigo-200'
                    : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="saOutgoingRole"
                    value="teacher"
                    checked={outgoingAdminRole === 'teacher'}
                    onChange={() => setOutgoingAdminRole('teacher')}
                    className="text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-xs font-bold text-slate-900">Demote to Teacher</span>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Classroom access only.</p>
              </label>

              <label
                className={`p-2.5 rounded-xl border flex flex-col justify-between cursor-pointer transition-all ${
                  outgoingAdminRole === 'stepped_down'
                    ? 'bg-amber-50 border-amber-300 ring-2 ring-amber-200'
                    : 'bg-white border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="saOutgoingRole"
                    value="stepped_down"
                    checked={outgoingAdminRole === 'stepped_down'}
                    onChange={() => setOutgoingAdminRole('stepped_down')}
                    className="text-amber-600 focus:ring-amber-500"
                  />
                  <span className="text-xs font-bold text-slate-900">Detach from School</span>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Removes all affiliation.</p>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isProcessing}
              className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isProcessing || !resolvedTargetEmail || !referenceTicket.trim()}
              className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {isProcessing ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  <span>Reassigning Administrator...</span>
                </>
              ) : (
                <>
                  <span className="material-icons-round text-sm">gavel</span>
                  <span>Confirm Admin Reassignment</span>
                </>
              )}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};
